import {MarketerRepository} from './MarketerRepository';
import {Marketer} from '../models/marketer.model';
import {MarketersStatistics} from '../models/MarketersStatistics';
import {MarketerAPIDataSource} from '../api/MarketerAPIDataSource';
import {Pagination} from '../../../Data/types/BaseResponse';
import {AppError, ErrorType} from '../../../Data/config/AppError';
import {CreateMarketerRequest} from '../models/CreateMarketer';
import {UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest} from '../models/UpdateMarketer';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export class MarketerRepositoryImpl implements MarketerRepository {
  async getAll (page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, search?: string): Promise<{ items: Marketer[]; pagination?: Pagination }> {
    try {
      const res = await MarketerAPIDataSource.getAll({page, limit, search: search?.trim() || undefined});

      const items = Array.isArray(res.items) ? res.items : [];

      return {
        items,
        pagination: res.pagination
      };
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء جلب قائمة المسوقين',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async getStatistics (): Promise<MarketersStatistics> {
    try {
      // Return basic statistics without making API calls
      return {
        totalMarketers: 0,
        activeMarketers: 0,
        inactiveMarketers: 0,
        regionDistribution: [],
        performanceMetrics: {
          averageSales: 0,
          topMarketer: null,
          totalRevenue: 0,
          monthlyGrowth: 0
        },
        recentActivities: {
          newMarketersThisMonth: 0,
          deactivatedMarketersThisMonth: 0,
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء جلب إحصائيات المسوقين',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async create (marketerData: CreateMarketerRequest): Promise<Marketer> {
    try {
      const response = await MarketerAPIDataSource.create(marketerData);
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء إنشاء المسوق',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async update (_id: number, _marketer: Partial<Marketer>): Promise<Marketer> {
    try {
      // TODO: Implement API call for updating marketer
      throw new AppError(
        'تحديث المسوق غير مدعوم حالياً',
        ErrorType.SERVER
      );
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء تحديث المسوق',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async updateProfile (id: number, profileData: UpdateMarketerProfileRequest): Promise<any> {
    try {
      const response = await MarketerAPIDataSource.updateProfile(id, profileData);
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء تحديث بيانات المسوق',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async updatePassword (id: number, passwordData: UpdateMarketerPasswordRequest): Promise<any> {
    try {
      const response = await MarketerAPIDataSource.updatePassword(id, passwordData);
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'حدث خطأ أثناء تغيير كلمة مرور المسوق',
        ErrorType.UNKNOWN,
        undefined,
        undefined,
        error
      );
    }
  }

  async delete (id: number): Promise<boolean> {
    try {
      const response = await MarketerAPIDataSource.delete(id);
      const result = response.data?.success || false;
      return result;
    } catch (error) {
      // Use StandardizedErrorHandler to extract server messages properly
      if (error instanceof AppError) {
        throw error;
      }
      // Convert to AppError to preserve server message
      const appError = AppError.fromApiError(error);
      throw appError;
    }
  }
}
